import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchMeta, fetchPayloadPrint } from '../services/webReportApi';
import { applyBranding } from '../styles/applyBranding';
import { PrintReportView } from './PrintReportView';
import type { ReportEnrichment, WebReportMeta, WebReportPayload } from '../types/webReport';
import styles from './PrintReport.module.css';

type Stage = 'loading' | 'ready' | 'error';

/**
 * /:slug/print — the page the headless PDF renderer (OutputGenerator) loads.
 * Authenticates with a short-lived print token (?pt=) minted by planscout-api;
 * there is no OTP gate, no analytics, and no interactivity here.
 *
 * Renderer contract:
 *  - success: window.__REPORT_READY = true + <div id="print-ready"> once fonts
 *    and images have settled — the renderer polls for this before page.pdf().
 *  - failure: <div id="print-error" data-reason="..."> — the renderer fails
 *    fast with the reason instead of waiting for its timeout.
 */
export function PrintReportPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const pt = searchParams.get('pt') || '';

  const [stage, setStage] = useState<Stage>('loading');
  const [errorReason, setErrorReason] = useState<string>('');
  const [meta, setMeta] = useState<WebReportMeta | null>(null);
  const [payload, setPayload] = useState<WebReportPayload | null>(null);
  const [enrichment, setEnrichment] = useState<ReportEnrichment | null>(null);

  useEffect(() => {
    if (!slug || !pt) {
      setErrorReason(!slug ? 'missing-slug' : 'missing-token');
      setStage('error');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchMeta(slug);
        if (cancelled) return;
        setMeta(m);
        applyBranding(m.advisor?.branding);
        const p = await fetchPayloadPrint(slug, pt);
        if (cancelled) return;
        if (!p.payload) throw new Error('empty-payload');
        setPayload(p.payload);
        setEnrichment(p.enrichment ?? null);
        setStage('ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorReason(err?.message || 'unknown');
        setStage('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, pt]);

  // Readiness signal: only after render, wait for webfonts (Albert Sans from
  // index.html) and images (advisor logo/photo) to settle so the PDF snapshot
  // never captures FOUT or half-loaded images.
  const [signalled, setSignalled] = useState(false);
  useEffect(() => {
    if (stage !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        await document.fonts.ready;
        await Promise.allSettled(
          Array.from(document.images).map((img) => img.decode().catch(() => undefined))
        );
      } finally {
        if (!cancelled) {
          window.__REPORT_READY = true;
          setSignalled(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [stage]);

  if (stage === 'error') {
    return (
      <div className={styles.center}>
        <div id="print-error" data-reason={errorReason} />
        <strong>This report can&rsquo;t be printed right now.</strong>
        <span>({errorReason})</span>
      </div>
    );
  }

  if (stage === 'ready' && payload) {
    return (
      <>
        <PrintReportView payload={payload} enrichment={enrichment} advisor={meta?.advisor ?? null} />
        {signalled && <div id="print-ready" hidden />}
      </>
    );
  }

  return (
    <div className={styles.center} aria-label="Loading">
      Preparing report&hellip;
    </div>
  );
}
